import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Loader, Spacer } from 'theSource/components';
import {
  BannerBodyContainerStyled,
  ButtonStyled,
  EnrollmentBannerContainerStyled,
  ImageStyled,
} from './EnrollmentBannerStyled';
import { PSModalWrapper } from 'enrollment/components/Shared/Modal';
import enrollmentBannerImg from 'theSource/assets/enrollmentBannerImg.svg';
import {
  EnrollmentContextResponseDto,
  GET_ENROLLMENT_CONTEXT_QUERY_KEY,
  useConfigurationQuery,
} from 'theSource/queries';
import {
  filterEnrollmentContexts,
  getCurrentContext,
  selectBodyText,
  selectHeaderText,
  isContinueEnrollment,
  lifeEventID,
} from './utils';
import { useApiMutation } from 'theSource/utils';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { setEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { REMOVE_LIFE_EVENT_QUERY_KEY } from 'enrollment/queries/useLifeEventQuery/utils';
import { getLastViewedPage, getPageRouter } from 'enrollment/utils/common/Utils';

export interface EnrollmentBannerProps {
  enrollmentContext: EnrollmentContextResponseDto | undefined;
  orgName: string;
}

export const EnrollmentBanner = ({
  enrollmentContext,
  orgName,
}: EnrollmentBannerProps) => {
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('pages.enrollmentBanner');
  const navigate = useNavigate();
  const [hasEnrollmentContext, setHasEnrollmentContext] = useState(false);
  const { configuration } = useConfigurationQuery();
  const continueEnrollment = isContinueEnrollment(enrollmentContext);
  const initSubLifeEventID = lifeEventID(enrollmentContext);
  const [subLifeEventID, setSubLifeEventID] = useState<string>('');
  const [unfinishedTask, setunfinishedTask] = useState<string>('');
  const lumosApiHost = configuration?.apiHost;
  const headers = [
    t('welcome'),
    t('lifeEventHeader'),
    t('openEnrollmentHeader'),
    t('welcomeBack'),
  ];
  const bodyTexts = [
    t('newHire'),
    t('welcomeBackMessage'),
    t('openEnrollmentBody'),
    t('dualEnrollment'),
    t('welcomeBackMessage'),
  ];

  const { isLoading, mutate: removeLE } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    {}
  >({
    queryKey: REMOVE_LIFE_EVENT_QUERY_KEY,
    queryKeysToInvalidate: GET_ENROLLMENT_CONTEXT_QUERY_KEY,
    method: 'post',
    endpoint: `${lumosApiHost}/v2/self_service/life_events/reject?subscriber_life_event_id=${subLifeEventID}`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  useEffect(() => {
    const currentContextLookup = getCurrentContext(enrollmentContext);
    if (currentContextLookup !== '') {
      const currentContext = enrollmentContext?.data.find((context) => {
        return context.enrollment_type_lookup_code === currentContextLookup;
      });
      setHasEnrollmentContext(filterEnrollmentContexts(enrollmentContext));
      const lastViewedPage = getLastViewedPage({
        currentCategory: currentContext?.current_category,
        requiredTask: currentContext?.required_tasks[0] || 'confirm',
      });
      setunfinishedTask(lastViewedPage);
      dispatch(setEnrollmentLatest(lastViewedPage));
    }
  }, [enrollmentContext, dispatch]);

  const navToEnrollment = () => {
    if (continueEnrollment) {
      navigate('/enrollment/' + getPageRouter(unfinishedTask));
    } else {
      navigate('/enrollment');
    }
  };

  useEffect(() => {
    if (hasEnrollmentContext) {
      setVisible(true);
    }
  }, [hasEnrollmentContext]);

  useEffect(() => {
    const handleReject = () => {
      setSubLifeEventID(initSubLifeEventID || '');
    };

    document
      .querySelector('button[id^="discardLE"][id$="_primary"]')
      ?.addEventListener('click', () => handleReject());
  }, [enrollmentContext, initSubLifeEventID, visible]);

  useEffect(() => {
    if (subLifeEventID !== '') {
      removeLE({});
    }
  }, [subLifeEventID, removeLE]);

  return visible ? (
    <EnrollmentBannerContainerStyled data-testid="enrollment-banner">
      <ImageStyled src={enrollmentBannerImg} />
      <BannerBodyContainerStyled>
        <Typography variant="h5">
          {selectHeaderText(enrollmentContext, headers, orgName)}
        </Typography>
        <Spacer height={3} />
        <Typography variant="body1">
          {selectBodyText(enrollmentContext, bodyTexts)}
        </Typography>
        <Spacer height={6} />
        {!isContinueEnrollment(enrollmentContext) && !initSubLifeEventID && (
          <ButtonStyled
            label={t('getStarted')}
            variant="secondary"
            fullWidth={false}
            onClick={navToEnrollment}
            testId="get-started"
          />
        )}
        {isLoading ? (
          <Loader />
        ) : (
          (isContinueEnrollment(enrollmentContext) || initSubLifeEventID) && (
            <div>
              <ps-button
                data-testid="continue-enroll"
                id="continue-enroll"
                level="primary"
                onClick={() => {
                  navToEnrollment();
                }}
                label={t('common:continueEnroll')}
              ></ps-button>
              {initSubLifeEventID && (
                <ps-button
                  data-testid="discard-life-event"
                  id="discard-life-event"
                  level="secondary"
                  extraClass="ml-3"
                  other-data={`${`data-bs-toggle='modal' data-bs-target='#discardLE'`}`}
                  label={t('common:discardLE')}
                ></ps-button>
              )}
            </div>
          )
        )}
        <PSModalWrapper
          id="discardLE"
          message={t('common:discardLEModalMessage')}
          title={t('common:discardLE')}
          primaryButton={t('common:discard')}
          primaryActive={true}
          secondaryButton={t('common:cancel')}
          extraClass="text-left"
        ></PSModalWrapper>
      </BannerBodyContainerStyled>
    </EnrollmentBannerContainerStyled>
  ) : null;
};
